import { useState, useEffect, useMemo, useCallback } from "react";
import { useApi } from "./useApi";

// In-memory cache for metadata (session lifetime)
const metadataCache = new Map();

/**
 * Hook to fetch and cache import job metadata including field mapping
 * @param {string|number} importId - Import job ID
 * @param {string} provider - Provider name
 * @param {string} category - Category name
 * @param {string|number} tenantId - Tenant ID
 * @returns {Object} { metadata, fieldMappingLookup, columns, isLoading, error, refresh }
 */
export const useImportJobMetadata = (
  importId,
  provider,
  category,
  tenantId
) => {
  const { getImportJobMetadata, loading, error } = useApi();
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [metadataError, setMetadataError] = useState(null);

  // Create cache key
  const cacheKey = useMemo(() => {
    if (!importId || !provider || !category || !tenantId) {
      return null;
    }
    return `${importId}_${provider}_${category}_${tenantId}`;
  }, [importId, provider, category, tenantId]);

  // Create fieldMappingLookup from metadata
  const fieldMappingLookup = useMemo(() => {
    if (!metadata || !metadata.fieldMapping) {
      return {};
    }
    // fieldMapping is already in the correct format: { "NIT": "circuit_id", ... }
    return metadata.fieldMapping;
  }, [metadata]);

  // Get columns from metadata
  const columns = useMemo(() => {
    if (!metadata || !metadata.columns) {
      return [];
    }
    return metadata.columns;
  }, [metadata]);

  // Fetch metadata
  const fetchMetadata = useCallback(async () => {
    if (!importId || !provider || !category || !tenantId) {
      setMetadata(null);
      setMetadataError(null);
      return;
    }

    // Check cache first
    if (cacheKey && metadataCache.has(cacheKey)) {
      const cachedMetadata = metadataCache.get(cacheKey);
      setMetadata(cachedMetadata);
      setIsLoading(false);
      setMetadataError(null);
      return;
    }

    setIsLoading(true);
    setMetadataError(null);

    try {
      const response = await getImportJobMetadata(
        importId,
        provider,
        category,
        tenantId
      );

      if (response && response.success) {
        // Cache the metadata
        if (cacheKey) {
          metadataCache.set(cacheKey, response);
        }
        setMetadata(response);
        setMetadataError(null);
      } else {
        // Backend returned a response but success is false
        const errorMessage =
          response?.message || response?.error || "Failed to fetch metadata";
        const error = new Error(errorMessage);
        error.response = response;
        throw error;
      }
    } catch (err) {
      // Extract detailed error message
      let errorMessage = "Failed to fetch metadata";
      if (err.message) {
        errorMessage = err.message;
      } else if (err.details?.message) {
        errorMessage = err.details.message;
      } else if (err.details?.error) {
        errorMessage = err.details.error;
      }

      // Log detailed error information for debugging
      console.error("Error fetching import job metadata:", {
        message: errorMessage,
        status: err.status,
        statusText: err.statusText,
        details: err.details,
        endpoint: err.endpoint,
        importId,
        provider,
        category,
        tenantId,
        fullError: err,
      });

      setMetadataError(errorMessage);
      setMetadata(null);

      // Clear cache entry if it exists (to force retry on next attempt)
      if (cacheKey && metadataCache.has(cacheKey)) {
        metadataCache.delete(cacheKey);
      }
    } finally {
      setIsLoading(false);
    }
  }, [importId, provider, category, tenantId, getImportJobMetadata, cacheKey]);

  // Refresh metadata (force reload from server)
  const refresh = useCallback(() => {
    if (cacheKey && metadataCache.has(cacheKey)) {
      metadataCache.delete(cacheKey);
    }
    fetchMetadata();
  }, [cacheKey, fetchMetadata]);

  // Fetch metadata on mount or when dependencies change
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  return {
    metadata,
    fieldMappingLookup,
    columns,
    isLoading: isLoading || loading,
    error: metadataError || error,
    refresh,
  };
};
